/**
 * The meters in the rail's Pro panel, filled in for a free reader (CB-61).
 *
 * `sidebars/modules/pro-usage.html` draws the panel on every page with a rail,
 * with its meters hidden, and `modules/pro-usage` shows the panel only where
 * the session hint names a free reader. This asks `GET /user/usage/` for that
 * reader's three counts and draws them. A member and a signed-out visitor
 * never cause the request, and a page with no panel makes none.
 *
 * **One answer serves a few minutes of pages.** The route sends a free
 * reader's answer `private, max-age=300` with `Vary: Cookie`, so the
 * browser's own cache keeps it for that session alone and a reader moving
 * through several articles asks once. A count that moves in that time (a word
 * saved on the page before) shows on a later answer. `usage-route.ts` says
 * why the page keeps nothing itself.
 *
 * **Reaching a limit changes the line, never the offer.** Where any count
 * stands at its limit, the panel says so warmly and the line about what Pro
 * lifts gives way to it. Both lines are in the markup, so the copy is the
 * template's.
 */

/** Where the counts come from. `usage-route.ts` names the same path. */
export const usagePath = "/user/usage/";

/** The three counts, as both the usage and the limits come. */
export interface UsageCounts {
  readonly words: number;
  readonly sentences: number;
  readonly exercises: number;
}

/** A free reader's answer, the only one this draws from. */
export interface FreeUsageAnswer {
  readonly usage: UsageCounts;
  readonly limits: UsageCounts;
}

/** What the panel draws with. */
export interface ProUsageProperties {
  readonly document: Document;
  readonly fetch: typeof globalThis.fetch;
}

/** The counts, in the order the panel lists them. */
const countNames = ["words", "sentences", "exercises"] as const;

/**
 * Whether an answer is a free reader's, with every count a number.
 * @param answer - What the route said, parsed.
 * @returns Whether it can be drawn.
 */
function isFreeAnswer(answer: unknown): answer is FreeUsageAnswer {
  if (typeof answer !== "object" || answer === null) {
    return false;
  }

  const { usage, limits } = answer as Partial<FreeUsageAnswer>;

  return countNames.every(
    (name) =>
      typeof usage?.[name] === "number" && typeof limits?.[name] === "number",
  );
}

/**
 * Draw the counts into one panel.
 *
 * Each row's meter takes its value and limit, and `high` one below four
 * fifths of the limit, as the account page's meters do, so a reader near a
 * limit sees the warning colour.
 * @param panel - The panel.
 * @param answer - The counts.
 */
export function drawUsage(panel: Element, answer: FreeUsageAnswer): void {
  let atLimit = false;

  for (const name of countNames) {
    const row = panel.querySelector(`[data-cb-usage="${name}"]`);
    const held = Math.min(answer.usage[name], answer.limits[name]);
    const limit = answer.limits[name];

    atLimit ||= held >= limit;

    const count = row?.querySelector(".cb-usage-count");
    const meter = row?.querySelector("meter");

    if (count) {
      count.textContent = `${String(held)} / ${String(limit)}`;
    }

    if (meter) {
      meter.setAttribute("max", String(limit));
      meter.setAttribute("high", String(Math.ceil(limit * 0.8) - 1));
      meter.setAttribute("value", String(held));
    }
  }

  panel.querySelector("[data-cb-usage-list]")?.removeAttribute("hidden");
  panel
    .querySelector("[data-cb-usage-line='open']")
    ?.toggleAttribute("hidden", atLimit);
  panel
    .querySelector("[data-cb-usage-line='limit']")
    ?.toggleAttribute("hidden", !atLimit);
}

/**
 * Fill every Pro panel on the page, for a free reader.
 * @param properties - The page and the network.
 * @returns Once the panels are drawn, or once there was nothing to draw.
 */
export async function startProUsage(
  properties: ProUsageProperties,
): Promise<void> {
  const { document } = properties;
  const panels = [...document.querySelectorAll("[data-cb-pro-usage]")];

  if (
    panels.length === 0 ||
    document.documentElement.dataset["cbMembership"] !== "free"
  ) {
    return;
  }

  let answer: FreeUsageAnswer;

  try {
    const response = await properties.fetch(usagePath, {
      credentials: "same-origin",
      headers: { accept: "application/json" },
    });
    const parsed: unknown = response.ok ? await response.json() : undefined;

    if (!isFreeAnswer(parsed)) {
      return;
    }

    answer = parsed;
  } catch {
    // The panel already offers Pro without meters, which is what it keeps.
    return;
  }

  for (const panel of panels) {
    drawUsage(panel, answer);
  }
}
